import React, { useState } from 'react';
import { ELSButton } from '@els/els-component-button-react';
import { ELSModalService } from '@els/els-component-modal-react';
import { ELSIcon, ELSTooltip } from '../../components/els.components';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import IconWithText from '../../components/icon-with-text/IconWithText.component';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import {
  ChatModeType,
  ChatModeTypeDisplayMap,
  MAX_PROMPT_LENGTH,
  MAX_PROMPT_LENGTH_DISPLAY,
} from './ai-chat.constants';
import { AppContext } from '../../components/app/AppProvider.component';
import { isFeatureFlagEnabled } from '../../utilities/featureFlag.utilities';
import AutoExpandTextarea from '../../components/auto-expand-textarea/AutoExpandTextArea.component';
import { ExamplePrompts } from '../../components/example-prompts/ExamplePrompts.component';
import { EntrySubmitProps } from './ai-chat.models';
import {
  getBrand,
  getIsDirectAccessByFeatureFlag,
  getOrderedPurchasedEntitlements,
  getSelectedIsbnFilters,
  isAdminUser,
  isInputBlocked,
  openBookFilterModal,
} from './ai-chat.utilities';
import { Brand } from '../../constants/common.constants';
import { ELSButtonSize, ELSButtonType } from '../../models/button.models';
import { getTokenIsbnsArray } from '../../components/app/app-state.utilities';
import {
  ChatEntryDto,
  ChatUserRoleDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';

export type AiChatInputBarProps = {
  prompt: string;
  isShowPromptExamples: boolean;
  chatEntries: ChatEntryDto[];
  handlePromptChange: (e) => void;
  handlePromptSend: (e) => void;
  handleOpenAdminModal: () => void;
  handleChatSubmit: (entrySubmitProps: EntrySubmitProps) => void;
  modalService: ELSModalService;
}

export const AiChatInputBar = (props: AiChatInputBarProps): JSX.Element => {
  const {
    prompt,
    isShowPromptExamples,
    chatEntries,
    handlePromptChange,
    handlePromptSend,
    handleOpenAdminModal,
    handleChatSubmit,
    modalService,
  } = props;

  const { appState, setAppState, appStateRef } = React.useContext(AppContext);
  const [hideChatModeTooltip, setHideChatModeTooltip] = useState(true);
  const [forceCloseChatModeTooltip, setForceCloseChatModeTooltip] = useState(false);

  const hasExistingMessages = chatEntries?.length > 0;

  const isAdminOverrideEnabled = (): boolean => {
    return isAdminUser() && appState.enableAdminOverride;
  };

  const getTextInputPlaceholder = (): string => {
    if (hasExistingMessages) {
      return 'Reply...';
    }

    if (appState.chatModeType === ChatModeType.TUTOR_MODE) {
      return 'What can I help you learn today?';
    }

    return 'Ask a question & get an answer from your materials';
  };

  const showChatModeTooltip = (): boolean => {
    const isTutorModeEnabled = isFeatureFlagEnabled(
      FEATURE_FLAG.ENABLE_TUTOR_MODE,
      appState.featureFlagsGrouped,
      appState.courseSectionId
    );

    if (!appState.chatUserRole || appState.chatUserRole === ChatUserRoleDto.INSTRUCTOR) {
      return false;
    }
    return isTutorModeEnabled;
  };

  const isbns = getTokenIsbnsArray();
  const isDirectAccessByFeatureFlag = getIsDirectAccessByFeatureFlag(
    appState.featureFlagsGrouped,
    appState.courseSectionId,
    isbns
  );
  const selectedIsbnFilters = getSelectedIsbnFilters(appState.selectedIsbnMap);
  const entitlements = getOrderedPurchasedEntitlements(appState.entitlements);
  const disabledMyBooks = appState.appLinkData?.outPostBody?.disabledMyBooks;
  const placeholderText = getTextInputPlaceholder();
  // Block submitting a new prompt while a blocking post-process job (flashcards,
  // quiz questions) is still in flight — mirrors the guard in handleChatSubmit.
  const isSubmitBlocked = isInputBlocked(appState);

  // Also block the keyboard (Enter) submit path — the disabled button only
  // covers clicks, and AutoExpandTextarea's onKeyPress would otherwise reach
  // handleChatSubmit and toast the isInputBlocked error.
  const handleGuardedPromptSend = (e) => {
    // Enter (no shift) or the send-button click is the submit gesture — matches
    // the check inside handlePromptSend.
    const isSubmitGesture = (e?.key === 'Enter' && !e?.shiftKey) || e?.type === 'click';
    if (isSubmitBlocked) {
      // Keep submission blocked, but let all other keystrokes through so the user
      // can keep typing during generation and post-process jobs.
      if (isSubmitGesture) {
        e?.preventDefault?.();
      }
      return;
    }
    handlePromptSend(e);
  };

  return (
    <div className="c-els-ai-chat__bottom">
      <div className="c-els-ai-chat__width">
        <div className="c-els-ai-chat__prompt">
          {isAdminOverrideEnabled() && (
            <div>
              <button className="u-els-anchorize" type="button" onClick={handleOpenAdminModal}>
                <IconWithText iconName="edit" iconPrefix="hmds">
                  Prompt override
                </IconWithText>
              </button>
            </div>
          )}

          {isShowPromptExamples && (
            <ExamplePrompts handleChatSubmit={handleChatSubmit} />
          )}

          <div className="c-els-field">
            <div className="c-els-ai-chat__input-wrapper">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="chat-prompt-input">
                <span className="u-els-hide-visually">{placeholderText}</span>
                <AutoExpandTextarea
                  className="c-els-ai-chat__input-textarea"
                  id="chat-prompt-input"
                  value={prompt}
                  handlePromptSend={handleGuardedPromptSend}
                  handlePromptChange={handlePromptChange}
                  placeholder={placeholderText}
                  maxLength={MAX_PROMPT_LENGTH}
                  minRows={1}
                  maxRows={10}
                />
              </label>
              <FlexLayout
                classes={['c-els-ai-chat__input-child-buttons']}
                modifiers={[FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.MIDDLE]}
              >
                <FlexItem>
                  <div className="u-els-nowrap">
                    <FlexLayout
                      classes={['c-els-ai-chat__materials']}
                      modifiers={[
                        FlexLayoutModifier.MIDDLE,
                        FlexLayoutModifier.SPACE_BETWEEN,
                        FlexLayoutModifier.GUTTERS,
                        FlexLayoutModifier.WRAP_AT_TABLET,
                      ]}
                    >
                      {!isDirectAccessByFeatureFlag &&
                        getBrand(appState) !== Brand.OSMOSIS_AI &&
                        entitlements &&
                        entitlements.length > 1 &&
                        !disabledMyBooks && (
                          <FlexItem>
                            <div className="c-naa-ai-chat-book-filters__button-wrapper">
                              <ELSButton
                                id="bookFiltersBtn"
                                className="c-els-ai-chat__choose-book-btn"
                                type={ELSButtonType.SECONDARY}
                                size={ELSButtonSize.X_SMALL}
                                onClick={() => {
                                  openBookFilterModal({ appStateRef, modalService });
                                }}
                              >
                                Choose books
                              </ELSButton>
                              {selectedIsbnFilters.length > 0 && (
                                <span className="c-naa-ai-chat-book-filters__selected-badge">
                                  {selectedIsbnFilters.length}
                                </span>
                              )}
                            </div>
                          </FlexItem>
                        )}
                      {showChatModeTooltip() && (
                        <FlexItem classes={['u-els-margin-left-1o4']}>
                          <ELSTooltip
                            showClose={false}
                            hide={forceCloseChatModeTooltip}
                            onOpenToolTip={() => {
                              setForceCloseChatModeTooltip(false);
                              setHideChatModeTooltip(false);
                            }}
                            onCloseToolTip={() => {
                              setForceCloseChatModeTooltip(false);
                              setHideChatModeTooltip(true);
                            }}
                            content={
                              <div>
                                <div className="u-els-padding-1o2">
                                  <button
                                    type="button"
                                    title="Study mode (default)"
                                    className="u-els-debuttonize"
                                    onClick={() => {
                                      setAppState((prevState) => ({
                                        ...prevState,
                                        chatModeType: ChatModeType.DEFAULT_STUDY_MODE,
                                      }));
                                      setForceCloseChatModeTooltip(true);
                                    }}
                                  >
                                    <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE]}>
                                      <FlexItem>
                                        <div className="c-els-ai-chat__selected-chat-mode-icon">
                                          {appState.chatModeType === ChatModeType.DEFAULT_STUDY_MODE && (
                                            <ELSIcon size="1x" prefix="hmds" name="checkmark" />
                                          )}
                                        </div>
                                      </FlexItem>
                                      <FlexItem>
                                        <div className="u-els-anchorize">
                                          <span>{ChatModeTypeDisplayMap[ChatModeType.DEFAULT_STUDY_MODE]} (default)</span>
                                          <br />
                                          <span className="u-els-color-n8 u-els-margin-top-1o4 u-els-display-inline-block">
                                            Gives you fast, clear answers with explanations
                                          </span>
                                        </div>
                                      </FlexItem>
                                    </FlexLayout>
                                  </button>
                                </div>
                                <div className="u-els-padding-1o2">
                                  <button
                                    type="button"
                                    title="Tutor mode"
                                    className="u-els-debuttonize"
                                    onClick={() => {
                                      setAppState((prevState) => ({
                                        ...prevState,
                                        chatModeType: ChatModeType.TUTOR_MODE,
                                      }));
                                      setForceCloseChatModeTooltip(true);
                                    }}
                                  >
                                    <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE]}>
                                      <FlexItem>
                                        <div className="c-els-ai-chat__selected-chat-mode-icon">
                                          {appState.chatModeType === ChatModeType.TUTOR_MODE && (
                                            <ELSIcon size="1x" prefix="hmds" name="checkmark" />
                                          )}
                                        </div>
                                      </FlexItem>
                                      <FlexItem>
                                        <div className="u-els-anchorize">
                                          <span>{ChatModeTypeDisplayMap[ChatModeType.TUTOR_MODE]}</span>
                                          <br />
                                          <span className="u-els-color-n8 u-els-margin-top-1o4 u-els-display-inline-block">
                                            Helps you work through topics step by step
                                          </span>
                                        </div>
                                      </FlexItem>
                                    </FlexLayout>
                                  </button>
                                </div>
                              </div>
                            }
                          >
                            <div className="u-els-font-size-meta">
                              <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE]}>
                                <FlexItem>{ChatModeTypeDisplayMap[appState.chatModeType]}</FlexItem>
                                <FlexItem>
                                  <div className="u-els-margin-left-1o4 u-els-margin-top-1o4 u-els-color-secondary">
                                    <ELSIcon
                                      size="1x1o4"
                                      prefix="hmds"
                                      name={hideChatModeTooltip ? 'chevron-down' : 'chevron-up'}
                                    />
                                  </div>
                                </FlexItem>
                              </FlexLayout>
                            </div>
                          </ELSTooltip>
                        </FlexItem>
                      )}
                    </FlexLayout>
                  </div>
                </FlexItem>
                <FlexItem>
                  <div>
                    <ELSButton
                      className="c-els-ai-chat__submit-btn"
                      id="chat-submit-btn"
                      type={ELSButtonType.PRIMARY}
                      size={ELSButtonSize.X_SMALL}
                      isDisabled={isSubmitBlocked}
                      onClick={(e) => {
                        handleGuardedPromptSend(e);
                      }}
                    >
                      <ELSIcon name="send" prefix="gizmo" size="1x" />
                      <span className="u-els-hide-visually">Submit chat</span>
                    </ELSButton>
                  </div>
                </FlexItem>
              </FlexLayout>
            </div>
          </div>

          {prompt?.length === MAX_PROMPT_LENGTH && (
            <div className="u-els-margin-top-1o2 u-els-color-n9">
              <FlexLayout
                modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.RIGHT, FlexLayoutModifier.MIDDLE]}
              >
                <FlexItem>
                  <ELSIcon name="flag" prefix="gizmo" size="1x" customClass="u-els-display-block" />
                </FlexItem>
                <FlexItem>We cannot exceed {MAX_PROMPT_LENGTH_DISPLAY} characters</FlexItem>
              </FlexLayout>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
